'use client';

import { useEffect, useRef } from 'react';
import {
  NEW_BUILD_DISMISSED_KEY,
  NEW_BUILD_POLL_INTERVAL_MS,
  NEW_BUILD_VERSION_PATH,
} from '@/constants';

async function fetchRemoteBuildId() {
  const response = await fetch(`${NEW_BUILD_VERSION_PATH}?t=${Date.now()}`, {
    cache: 'no-store',
  });

  if (!response.ok) {
    return null;
  }

  const payload = await response.json();
  const buildId = String(payload?.buildId || '').trim();
  return buildId || null;
}

function readDismissedBuildId() {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    return String(window.sessionStorage.getItem(NEW_BUILD_DISMISSED_KEY) || '').trim();
  } catch {
    return '';
  }
}

function writeDismissedBuildId(buildId) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(NEW_BUILD_DISMISSED_KEY, buildId);
  } catch {
    // Ignore quota / private-mode failures.
  }
}

/**
 * Polls /version.json and invokes onNewBuild when the remote buildId diverges
 * from the baseline captured when this tab loaded.
 */
export default function useNewBuildNotifier({ onNewBuild, enabled = true } = {}) {
  const baselineRef = useRef(null);
  const notifiedRef = useRef('');
  const onNewBuildRef = useRef(onNewBuild);

  useEffect(() => {
    onNewBuildRef.current = onNewBuild;
  }, [onNewBuild]);

  useEffect(() => {
    if (!enabled || process.env.NODE_ENV === 'development') {
      return undefined;
    }

    let cancelled = false;

    const checkForNewBuild = async () => {
      try {
        const remoteBuildId = await fetchRemoteBuildId();

        if (cancelled || !remoteBuildId) {
          return;
        }

        if (!baselineRef.current) {
          baselineRef.current = remoteBuildId;
          return;
        }

        if (remoteBuildId === baselineRef.current) {
          return;
        }

        if (
          remoteBuildId === notifiedRef.current ||
          remoteBuildId === readDismissedBuildId()
        ) {
          return;
        }

        notifiedRef.current = remoteBuildId;
        onNewBuildRef.current?.({
          buildId: remoteBuildId,
          dismiss: () => writeDismissedBuildId(remoteBuildId),
        });
      } catch {
        // Silent — never toast on network / parse failures.
      }
    };

    checkForNewBuild();
    const intervalId = window.setInterval(checkForNewBuild, NEW_BUILD_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [enabled]);
}
